import { useEffect, useMemo } from 'react';

import { UserRoles } from '@dynamika-shared/types/auth';
import { useAuthSlice } from '@restore/auth/auth.slice';
import {
  BreadcrumbId,
  ShortBreadcrumb,
} from '@restore/breadcrumbs/breadcrumbs.types';
import { useBreadcrumbsSlice } from '@restore/breadcrumbs/breadcrumbs.slice';

import { menuConfig } from '../constants/menu';
import useCurrentPath from './useCurentPath';

type BreadcrumbType = {
  title: string;
  path?: string;
};

type UseBreadcrumbsOptions = {
  // When false the hook computes nothing into the bar — neither the explicit
  // crumbs nor the auto-derived trail are dispatched. Use this when an ancestor
  // route owns the breadcrumb trail (e.g. a shared tab rendered under a parent
  // that already sets the full trail), so the child does not overwrite it.
  enabled?: boolean;
};

/**
 * Derives the breadcrumb trail for a route from the role-specific menu config.
 * Pure: depends only on its arguments, so it is trivially memoizable/testable.
 */
const getBreadcrumbsForPath = (
  currentPath: string | undefined,
  role: UserRoles | undefined
): BreadcrumbType[] => {
  const menuRoutes = menuConfig[role as UserRoles]?.flatMap(item => [
    item,
    ...(item.children || []),
  ]);

  const sections = currentPath?.split('/').filter(Boolean) || ['home'];

  return sections.reduce<BreadcrumbType[]>((acc, _section, index) => {
    const path = `/${sections.slice(0, index + 1).join('/')}`;
    const route = menuRoutes?.find(item => item.path === path);
    const isLast = index === sections.length - 1;

    if (route) {
      acc.push({ title: route.title, path: isLast ? undefined : path });
    }
    return acc;
  }, []);
};

export const useBreadcrumbs = (
  crumbs?: ShortBreadcrumb[],
  ids?: BreadcrumbId[],
  options?: UseBreadcrumbsOptions
) => {
  const { enabled = true } = options ?? {};
  const currentPath = useCurrentPath();
  const role = useAuthSlice(s => s.data?.selectedRole) as UserRoles | undefined;

  const [setBreadcrumbs, makeBreadcrumbs] = useBreadcrumbsSlice(
    (state, actions) => [actions.setBreadcrumbs, actions.makeBreadcrumbs]
  );

  const autoBreadcrumbs = useMemo(
    () => getBreadcrumbsForPath(currentPath, role),
    [currentPath, role]
  );

  // Callers pass `crumbs`/`ids` as fresh array literals every render, so the
  // effect must compare them by value (not reference) to avoid re-dispatching
  // on every render. `autoBreadcrumbs` is already memoized, so its reference is
  // a sufficient (and cheaper) value signal — only used in auto-calc mode.
  const explicitKey = crumbs ? JSON.stringify({ crumbs, ids }) : null;
  const autoCalcDep = crumbs ? null : autoBreadcrumbs;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (crumbs) {
      makeBreadcrumbs({ crumbs, ids });
    } else {
      setBreadcrumbs(autoBreadcrumbs);
    }
    // Slice-hook actions are intentionally excluded: they are recreated every
    // render and would cause an infinite dispatch loop (see code-style rules).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explicitKey, autoCalcDep, enabled]);

  return autoBreadcrumbs;
};
